import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, UtensilsCrossed, LayoutGrid, QrCode, LogOut, Settings, Menu, X, Store, Users, ChevronDown, Check, BarChart3, PanelLeftClose, PanelLeftOpen, TicketPercent, Clock3, ConciergeBell, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOutlet } from "@/context/OutletContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BottomSheet } from "@/components/ui/bottom-sheet";

const navBase = [
  { to: "/admin", label: "Dashboard", englishLabel: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/orders", label: "Pemesanan", displayLabel: "Pemesanan / Order Board", englishLabel: "Orders / Order Board", icon: ShoppingBag },
  { to: "/admin/menu", label: "Menu", displayLabel: "Menu Hidangan", englishLabel: "Menu Items", icon: UtensilsCrossed },
  { to: "/admin/categories", label: "Kategori", englishLabel: "Categories", icon: LayoutGrid },
  { to: "/admin/tables", label: "Meja & QR", displayLabel: "Meja & QR Code", englishLabel: "Tables & QR Codes", icon: QrCode },
  { to: "/admin/vouchers", label: "Voucher", displayLabel: "Voucher Promosi", englishLabel: "Promo Vouchers", icon: TicketPercent },
  { to: "/admin/reports", label: "Laporan", displayLabel: "Laporan & Analitik", englishLabel: "Reports & Analytics", icon: BarChart3 },
  { to: "/admin/settings", label: "Pengaturan Outlet", englishLabel: "Outlet Settings", icon: Settings },
];
const navSuperAdmin = [
  { to: "/admin/outlets", label: "Outlets", displayLabel: "Kelola Outlet", englishLabel: "Manage Outlets", icon: Store },
  { to: "/admin/users", label: "Users", displayLabel: "Kelola Staf", englishLabel: "Manage Staff", icon: Users },
];

function BrandBlock({ activeOutlet, logoSrc, compact, english = false }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="w-10 h-10 rounded-xl bg-white grid place-items-center overflow-hidden flex-shrink-0">
        {logoSrc ? (
          <img src={logoSrc} alt={`Logo ${activeOutlet?.name || (english ? "restaurant" : "restoran")}`} className="w-full h-full object-contain p-1"/>
        ) : (
          <span className="font-display font-bold text-primary text-sm">
            {(activeOutlet?.name || "R").slice(0, 1)}
          </span>
        )}
      </div>
      {!compact && (
        <div className="min-w-0">
          <p className="font-display font-semibold truncate text-xl text-[#943512]">{activeOutlet?.name || (english ? "Restaurant" : "Restoran")}</p>
          <p className="mt-1 inline-flex rounded-full bg-[#ffddb1] px-2 py-0.5 text-[9px] font-semibold text-[#614000]">{english ? "Restaurant Operations" : "Operasional Restoran"}</p>
        </div>
      )}
    </div>
  );
}

function OutletSwitcher({ english = false }) {
  const { outlets, activeOutlet, setActiveOutletId } = useOutlet();
  const { isSuperAdmin } = useAuth();
  if (!outlets?.length) return null;
  // Outlet admin can't switch — only see their own outlet as static label
  if (!isSuperAdmin || outlets.length <= 1) {
    return (
      <div className="admin-outlet-switcher" data-testid="outlet-static-label">
        <Store className="w-3.5 h-3.5 text-primary"/> {activeOutlet?.name || "-"}
      </div>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="admin-outlet-switcher hover:bg-[#e5e2de]" data-testid="outlet-switcher-btn" aria-label={`${english ? "Select outlet" : "Pilih outlet"}, ${activeOutlet?.name || (english ? "none selected" : "belum dipilih")}`}>
          <Store className="w-3.5 h-3.5 text-primary"/>
          <span className="max-w-[80px] truncate sm:max-w-[140px]">{activeOutlet?.name || (english ? "Select outlet" : "Pilih outlet")}</span>
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground"/>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent lang={english ? "en" : undefined} className="w-56" align="end">
        {outlets.map((o) => (
          <DropdownMenuItem key={o.id} onClick={() => setActiveOutletId(o.id)} data-testid={`switch-outlet-${o.slug}`}
            className="flex items-center justify-between cursor-pointer">
            <span className="truncate">{o.name}</span>
            {o.id === activeOutlet?.id && <Check className="w-4 h-4 text-primary"/>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NavItems({ nav, onClick, compact, english = false }) {
  const { pathname } = useLocation();
  return (
    <nav aria-label={english ? "Admin navigation" : "Navigasi admin"} className={`p-3 flex-1 space-y-1 overflow-y-auto ${compact ? "px-2" : ""}`}>
      {nav.map((n) => (
        <NavLink key={n.to} to={n.end && pathname === `${n.to}/` ? pathname : n.to} end={n.end} onClick={onClick} title={compact ? english ? n.englishLabel : n.label : undefined}
          className={({ isActive }) => `admin-nav-link flex items-center gap-3 rounded-xl text-sm transition-colors duration-150 ${compact ? "justify-center px-0 py-3" : "px-3 py-3"} ${isActive || (n.end && pathname.replace(/\/$/, "") === n.to) ? "active" : ""}`}
          data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}-link`}
        >
          <n.icon className="w-4 h-4 flex-shrink-0" /> <span className={compact ? "sr-only" : ""}>{english ? n.englishLabel : n.displayLabel || n.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout, isSuperAdmin } = useAuth();
  const { activeOutlet, logoSrc } = useOutlet();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("resto_sidebar_collapsed") === "1");
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => { const timer = setInterval(() => setClock(new Date()), 60000); return () => clearInterval(timer); }, []);
  const toggleCollapsed = () =>
    setCollapsed((c) => {
      localStorage.setItem("resto_sidebar_collapsed", c ? "0" : "1");
      return !c;
    });

  const nav = [...navBase, ...(isSuperAdmin ? navSuperAdmin : [])];
  const isDashboard = pathname.replace(/\/$/, "") === "/admin";
  const currentPage = nav.find((item) => item.to === pathname.replace(/\/$/, ""))?.displayLabel || nav.find((item) => item.to === pathname.replace(/\/$/, ""))?.label || "Dashboard";

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div lang={isDashboard ? "en" : undefined} className="admin-shell flex">
      <a href="#admin-main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:rounded-lg focus:bg-white focus:px-4 focus:py-3 focus:text-teak">{isDashboard ? "Skip to content" : "Langsung ke konten"}</a>
      <aside className={`admin-sidebar hidden md:flex flex-col fixed inset-y-0 left-0 transition-all duration-200 ${collapsed ? "w-16" : "w-72"}`} data-testid="admin-sidebar">
        <div className={`border-b border-border flex items-center ${collapsed ? "flex-col gap-2 p-2" : "justify-between gap-2 p-4"}`}>
          <BrandBlock activeOutlet={activeOutlet} logoSrc={logoSrc} compact={collapsed} english={isDashboard} />
          <button
            onClick={toggleCollapsed}
            className="w-8 h-8 rounded-lg border border-border grid place-items-center hover:bg-accent transition-colors flex-shrink-0"
            data-testid="sidebar-collapse-btn"
            title={isDashboard ? collapsed ? "Expand menu" : "Collapse menu" : collapsed ? "Perlebar menu" : "Kecilkan menu"}
            aria-label={isDashboard ? collapsed ? "Expand menu" : "Collapse menu" : collapsed ? "Perlebar menu" : "Kecilkan menu"}
            aria-expanded={!collapsed}
          >
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>
        <NavItems nav={nav} compact={collapsed} english={isDashboard} />
        <div className={`rounded-xl bg-[#f0ede9] ${collapsed ? "m-2 p-0" : "m-3 p-3"}`}>
          <div className={`mb-3 flex items-center gap-2.5 ${collapsed ? "hidden" : ""}`}>
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#943512] text-white"><UserRound className="h-4 w-4" /></span><div className="min-w-0"><p className="truncate text-[13px] font-semibold" data-testid="admin-user-email">{user?.email}</p><p className="mt-0.5 text-[10px] font-medium text-[#7e5713]">{isSuperAdmin ? "Super Admin" : "Outlet Admin"}</p></div>
          </div>
          <Button variant="ghost" onClick={handleLogout} title={collapsed ? "Logout" : undefined} className={`w-full rounded-lg bg-[#e5e2de] ${collapsed ? "justify-center px-0" : "justify-center"}`} data-testid="logout-btn">
            <LogOut className={`w-4 h-4 ${collapsed ? "" : "mr-2"}`} /> <span className={collapsed ? "sr-only" : ""}>Logout</span>
          </Button>
        </div>
      </aside>

      <div className="admin-sidebar md:hidden fixed top-0 inset-x-0 z-30 border-b border-border h-16 flex items-center justify-between px-4">
        <BrandBlock activeOutlet={activeOutlet} logoSrc={logoSrc} english={isDashboard} />
        <button onClick={() => setOpen(true)} aria-label={isDashboard ? "Open admin navigation" : "Buka navigasi admin"} aria-expanded={open} className="w-10 h-10 rounded-lg border border-white/20 grid place-items-center" data-testid="mobile-menu-btn">
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {open && (
        <BottomSheet side title={isDashboard ? "Admin navigation" : "Navigasi admin"} description={isDashboard ? "Choose a page or close this menu to return to the dashboard." : undefined} lang={isDashboard ? "en" : undefined} onClose={() => setOpen(false)}>
          <div className="flex min-h-0 flex-1 flex-col" data-testid="mobile-drawer">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <BrandBlock activeOutlet={activeOutlet} logoSrc={logoSrc} english={isDashboard} />
              <button onClick={() => setOpen(false)} aria-label={isDashboard ? "Close admin navigation" : "Tutup navigasi admin"} className="w-9 h-9 shrink-0 rounded-lg border border-border grid place-items-center">
                <X className="w-4 h-4"/>
              </button>
            </div>
            <NavItems nav={nav} onClick={() => setOpen(false)} english={isDashboard} />
            <div className="p-3 border-t border-border">
              <p className="px-3 text-xs text-muted-foreground mb-2 truncate">{user?.email}</p>
              <Button variant="outline" onClick={handleLogout} className="w-full justify-start rounded-lg">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </BottomSheet>
      )}

      <main id="admin-main" tabIndex={-1} className={`flex-1 min-w-0 max-w-full pt-16 md:pt-0 transition-all duration-200 ${collapsed ? "md:ml-16" : "md:ml-72"}`}>
        {/* Sticky top bar with outlet switcher */}
        <div className="admin-topbar">
          <span className="hidden min-w-0 items-center gap-2 text-xs font-medium text-muted-foreground lg:flex"><ConciergeBell className="h-5 w-5 shrink-0 text-[#943512]" /><span className="truncate" data-testid="admin-current-page">{isDashboard ? "Management / Dashboard" : `Ruang Pengelolaan / ${currentPage}`}</span></span>
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
            <time data-testid="admin-datetime" className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-accent px-2 py-2 text-[10px] sm:px-3 sm:text-xs" dateTime={clock.toISOString()}>
              <Clock3 className="h-4 w-4 shrink-0 text-[#943512]" />
              <span className="flex flex-col sm:flex-row sm:items-center sm:gap-1.5">
                <span>{new Intl.DateTimeFormat(isDashboard ? "en-GB" : "id-ID", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Jakarta" }).format(clock)}</span>
                <span aria-hidden="true" className="hidden sm:inline">·</span>
                <span>{new Intl.DateTimeFormat(isDashboard ? "en-GB" : "id-ID", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" }).format(clock).replace(".", ":")} WIB</span>
              </span>
            </time>
            <OutletSwitcher english={isDashboard} />
          </div>
        </div>
        <div className={`admin-content ${isDashboard ? "px-4 pb-4 pt-1 md:px-8 md:pb-8" : "p-4 md:p-8"}`}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
